import {
  Archive,
  Download,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Plus,
  RotateCcw,
  Search,
  Table as TableIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { PurgeArchivedButton } from '@/features/crm/components/PurgeArchivedButton';
import { exportCsv, type ApiError } from '@/core/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatMoney } from '@/lib/money';
import { sumBase } from '@/lib/money-totals';
import { BASE as CONTRACTS_BASE } from '../api';
import { CONTRACT_STATUSES, statusLabel, statusVariant } from '../constants';
import type { Contract } from '../types';
import { useArchiveContract, useContracts, usePurgeContract, useRestoreContract } from '../queries';
import { useContractsUi, type ContractsViewMode } from '../store/viewPrefs';

/** Columns the backend lets us order by — keeps typos out of the URL. */
type ContractOrderingColumn = 'title' | 'status' | 'base_amount' | 'signed_at' | 'created' | 'modified';

const VIEW_MODES: readonly ViewModeOption<ContractsViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by status', icon: Layers },
];

const usd = (v: string | null): string => (v == null ? '—' : (formatMoney(v, 'USD') ?? '—'));

export function ContractsListPage() {
  const navigate = useNavigate();
  const view = useContractsUi((s) => s.view);
  const setView = useContractsUi((s) => s.setView);
  const showArchived = useContractsUi((s) => s.showArchived);
  const setShowArchived = useContractsUi((s) => s.setShowArchived);
  const ordering = useContractsUi((s) => s.ordering);
  const setOrdering = useContractsUi((s) => s.setOrdering);

  const [searchInput, setSearchInput] = useState('');
  // Live, debounced search — fires 300ms after typing stops (no Search button).
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);

  const { data, isPending, isError, error, isFetching } = useContracts({
    page,
    search: search || undefined,
    status: status === 'all' ? undefined : status,
    archived: showArchived ? true : undefined,
    ordering,
  });

  const archive = useArchiveContract();
  const restore = useRestoreContract();
  const purge = usePurgeContract();

  const results = data?.results ?? [];
  const onArchive = (c: Contract) => archive.mutate(c.id);
  const onRestore = (c: Contract) => restore.mutate(c.id);
  const onPurge = (c: Contract) => purge.mutate(c.id);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Contracts</h1>
        <Button onClick={() => navigate('/crm/contracts/new')}>
          <Plus /> New contract
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative max-w-sm flex-1">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setPage(1);
                setSearchInput(e.target.value);
              }}
              placeholder="Search contracts…"
              className="pl-8"
              aria-label="Search contracts"
            />
          </div>
          <Select
            value={status}
            onValueChange={(v) => {
              setPage(1);
              setStatus(v);
            }}
          >
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {CONTRACT_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant={showArchived ? 'default' : 'outline'}
            onClick={() => {
              setPage(1);
              setShowArchived(!showArchived);
            }}
            aria-pressed={showArchived}
            aria-label={showArchived ? 'Showing archived contracts' : 'Show archived contracts'}
            title={showArchived ? 'Showing archived contracts' : 'Show archived contracts'}
          >
            <Archive className="size-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              // Mirror the live list filters and add `export=csv`; the backend
              // returns `Content-Disposition: attachment`, so this navigation
              // downloads `contracts.csv` without leaving the SPA.
              void exportCsv(CONTRACTS_BASE, {
                search: search || undefined,
                status: status === 'all' ? undefined : status,
                archived: showArchived ? true : undefined,
                ordering,
                export: 'csv',
              });
            }}
            aria-label="Export contracts as CSV"
            title="Export contracts as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">{(error as unknown as ApiError).message}</p>
      ) : isPending ? (
        <ContractsSkeleton />
      ) : results.length === 0 ? (
        <ContractsTable
          contracts={[]}
          onRowClick={() => undefined}
          emptyMessage={showArchived ? 'No archived contracts.' : undefined}
          onArchive={onArchive}
          onRestore={onRestore}
          onPurge={onPurge}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'table' ? (
        <ContractsTable
          contracts={results}
          onRowClick={(c) => navigate(`/crm/contracts/${c.public_id}`)}
          onArchive={onArchive}
          onRestore={onRestore}
          onPurge={onPurge}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'list' ? (
        <ContractsList contracts={results} onOpen={(c) => navigate(`/crm/contracts/${c.public_id}`)} />
      ) : view === 'cards' ? (
        <ContractsCards contracts={results} onOpen={(c) => navigate(`/crm/contracts/${c.public_id}`)} />
      ) : (
        <ContractsGroupedByStatus
          contracts={results}
          onOpen={(c) => navigate(`/crm/contracts/${c.public_id}`)}
        />
      )}

      {/* Page-level USD aggregate — contracts snapshot `base_amount` at creation
          so this is a real currency-correct sum across the visible page. */}
      {results.length > 0 ? (
        <p className="text-muted-foreground text-sm">
          Page total: {formatMoney(sumBase(results, 'base_amount'), 'USD')}
        </p>
      ) : null}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'contract' : 'contracts'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface ListProps {
  contracts: Contract[];
  onOpen: (c: Contract) => void;
}

interface ContractsTableProps {
  contracts: Contract[];
  onRowClick: (c: Contract) => void;
  emptyMessage?: string;
  onArchive: (c: Contract) => void;
  onRestore: (c: Contract) => void;
  onPurge: (c: Contract) => void;
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}

function ContractsTable({
  contracts,
  onRowClick,
  emptyMessage,
  onArchive,
  onRestore,
  onPurge,
  ordering,
  onOrderingChange,
}: ContractsTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<ContractOrderingColumn>
              column="title"
              value={ordering}
              onChange={onOrderingChange}
            >
              Title
            </SortableTableHead>
            <SortableTableHead<ContractOrderingColumn>
              column="status"
              value={ordering}
              onChange={onOrderingChange}
            >
              Status
            </SortableTableHead>
            <SortableTableHead<ContractOrderingColumn>
              column="base_amount"
              value={ordering}
              onChange={onOrderingChange}
            >
              Amount (USD)
            </SortableTableHead>
            <SortableTableHead<ContractOrderingColumn>
              column="signed_at"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden md:table-cell"
            >
              Signed
            </SortableTableHead>
            <TableHead className="w-16 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {contracts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                {emptyMessage ?? 'No contracts found.'}
              </TableCell>
            </TableRow>
          ) : (
            contracts.map((c) => (
              <TableRow key={c.id} className="cursor-pointer" onClick={() => onRowClick(c)}>
                <TableCell className="font-medium">{c.title}</TableCell>
                <TableCell>
                  <Badge variant={statusVariant(c.status)}>{statusLabel(c.status)}</Badge>
                </TableCell>
                <TableCell className="tabular-nums">{usd(c.base_amount)}</TableCell>
                <TableCell className="text-muted-foreground hidden md:table-cell">
                  {c.signed_at ? new Date(c.signed_at).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell className="text-right">
                  {c.is_archived ? (
                    <span className="inline-flex items-center justify-end">
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Restore ${c.title}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRestore(c);
                        }}
                      >
                        <RotateCcw className="size-4" />
                      </Button>
                      <PurgeArchivedButton name={c.title} onPurge={() => onPurge(c)} />
                    </span>
                  ) : (
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={`Archive ${c.title}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onArchive(c);
                      }}
                    >
                      <Archive className="size-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function ContractsCards({ contracts, onOpen }: ListProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {contracts.map((c) => (
        <Card
          key={c.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(c)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(c);
            }
          }}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
        >
          <CardHeader>
            <CardTitle className="text-base">{c.title}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant={statusVariant(c.status)}>{statusLabel(c.status)}</Badge>
            <span className="text-muted-foreground tabular-nums">{usd(c.base_amount)}</span>
            <span className="text-muted-foreground ml-auto">
              {new Date(c.created).toLocaleDateString()}
            </span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ContractsList({ contracts, onOpen }: ListProps) {
  return (
    <ul className="divide-y rounded-md border">
      {contracts.map((c) => (
        <li key={c.id}>
          <button
            type="button"
            onClick={() => onOpen(c)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left"
          >
            <Badge variant={statusVariant(c.status)} className="shrink-0">
              {statusLabel(c.status)}
            </Badge>
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{c.title}</span>
            <span className="text-muted-foreground hidden text-xs tabular-nums sm:inline">
              {usd(c.base_amount)}
            </span>
            <span className="text-muted-foreground text-xs">
              {new Date(c.created).toLocaleDateString()}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function ContractsGroupedByStatus({ contracts, onOpen }: ListProps) {
  // Bucket by status, preserving the CONTRACT_STATUSES order
  // (draft → sent → signed → cancelled).
  const byStatus = useMemo(() => {
    const map = new Map<string, Contract[]>();
    for (const status of CONTRACT_STATUSES) map.set(status.value, []);
    for (const contract of contracts) {
      const list = map.get(contract.status) ?? [];
      list.push(contract);
      map.set(contract.status, list);
    }
    return map;
  }, [contracts]);

  return (
    <div className="space-y-6">
      {CONTRACT_STATUSES.map(({ value, label }) => {
        const bucket = byStatus.get(value) ?? [];
        if (bucket.length === 0) return null;
        // Contracts snapshot `base_amount` in USD at creation — safe to aggregate.
        const subtotal = sumBase(bucket, 'base_amount');
        return (
          <section key={value} aria-labelledby={`status-${value}`}>
            <h2
              id={`status-${value}`}
              className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
            >
              {label}
              <Badge variant="outline" className="text-[10px]">
                {bucket.length}
              </Badge>
              {subtotal > 0 ? (
                <span className="text-foreground/80 ml-auto normal-case tracking-normal">
                  {formatMoney(subtotal, 'USD')}
                </span>
              ) : null}
            </h2>
            <ContractsCards contracts={bucket} onOpen={onOpen} />
          </section>
        );
      })}
    </div>
  );
}

function ContractsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
